import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { isValidElement } from 'react';

import { getCellValue, splitHighlightParts } from './dataTableV2Utils';
import type { ResolvedColumn } from './dataTableV2Utils';

/** Выравнивание ячейки (align колонки) в виде style-фрагмента. */
export const textAlignStyle = (align: string | null | undefined): CSSProperties | undefined => (
  align ? { textAlign: align as CSSProperties['textAlign'] } : undefined
);

/** Значение ячейки в ReactNode: примитивы и JSX — как есть, остальное — строкой. */
export const renderCellValue = (value: unknown): ReactNode => {
  if (value === undefined || value === null) {
    return '';
  }

  const type = typeof value;

  if (type === 'string' || type === 'number' || type === 'boolean') {
    return value as ReactNode;
  }

  if (isValidElement(value)) {
    return value;
  }

  if (Array.isArray(value) && value.every(item => isValidElement(item))) {
    return value;
  }

  return String(value);
};

/**
 * Текст ячейки с подсветкой быстрого поиска: вхождения запроса оборачиваются
 * в <mark>. Подсвечиваются только примитивные значения (строки/числа) —
 * кастомные JSX-ячейки (ссылки, бейджи) остаются как есть.
 */
const HighlightedText = ({ value, query }: { value: string; query: string }) => {
  const parts = splitHighlightParts(value, query);

  if (!parts) {
    return <>{ value }</>;
  }

  return (
    <>
      { parts.map((part, index) => (part.match
        ? <mark key={ index }>{ part.text }</mark>
        : <span key={ index }>{ part.text }</span>
      )) }
    </>
  );
};

/** Содержимое дефолтной td: значение колонки + подсветка поиска, когда он активен. */
export const renderDefaultCellContent = (
  column: ResolvedColumn,
  row: any,
  highlightQuery: string
) => {
  const value = getCellValue(column.source, row);

  if (highlightQuery && (typeof value === 'string' || typeof value === 'number')) {
    return <HighlightedText value={ String(value) } query={ highlightQuery } />;
  }

  return renderCellValue(value);
};

/**
 * Тултип полного текста у обрезанного ellipsis-ом содержимого (td ячеек и
 * span заголовка th): если текст не влез (scrollWidth > clientWidth при
 * overflow:hidden + nowrap), ставим нативный title. Без ре-рендеров — правим
 * DOM-атрибут напрямую; React его не знает и не перезатирает, а каждый заход
 * пересчитывает актуальное содержимое.
 */
export const handleCellTitle = (event: ReactMouseEvent<HTMLElement>) => {
  const el = event.currentTarget;

  if (el.scrollWidth > el.clientWidth) {
    el.title = el.textContent ?? '';
  } else if (el.title) {
    el.removeAttribute('title');
  }
};

/** Паддинги th (.75rem×2) + шеврон сортировки + воронка мультифильтра. */
const HEADER_EXTRAS_WIDTH = 50;

/**
 * Естественная ширина содержимого ячейки колонки (для автоподбора двойным
 * кликом). td: Range по содержимому — полная ширина текста БЕЗ клипа
 * ellipsis-ом и без паддингов ячейки (scrollWidth у влезающего контента равен
 * ширине колонки — колонка не смогла бы СЖАТЬСЯ). th: спан-заголовок
 * (scrollWidth отдаёт полный текст) + паддинги и место шеврона/воронки.
 * 0 = измерить не удалось (jsdom/скрытая таблица) — вызывающий пропускает.
 */
export const measureColumnCell = (cell: Element, isHeaderCell: boolean): number => {
  if (isHeaderCell) {
    const span = cell.querySelector('span.text-truncate') as HTMLElement | null;

    return (span ? span.scrollWidth : (cell as HTMLElement).scrollWidth) + HEADER_EXTRAS_WIDTH;
  }

  const range = document.createRange();

  range.selectNodeContents(cell);

  // rect отсутствует, если Range API без layout (jsdom) — тогда scrollWidth-фолбэк;
  // width 0 — ВАЛИДНЫЙ ответ «контент пуст»: ячейка = только паддинги
  const rect = range.getBoundingClientRect?.();

  if (rect) {
    const { paddingLeft, paddingRight } = window.getComputedStyle(cell);
    const padding = Number.parseFloat(paddingLeft) + Number.parseFloat(paddingRight);

    return Math.ceil(rect.width) + (Number.isFinite(padding) ? padding : 0);
  }

  // jsdom — scrollWidth уже с паддингами
  return (cell as HTMLElement).scrollWidth;
};
